import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { CognitoAuthService } from '@commons/services/cognito-auth/cognito-auth.service';
import { StorageService } from '@commons/services/storage/storage.service';
import { STORAGE_TYPE } from '@commons/models';
import { LOBS, LOBS_DATA, WAREHOUSES } from '@commons/constants';
import { persistSessionEmail } from '@commons/utils';
/**
 * Route guard that verifies user authentication with AWS Cognito and rehydrates session data if needed.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard implements CanActivate {
  /**
   * Initializes the AuthenticationGuard.
   * @param router - Router service for navigation and redirection.
   * @param storageService - Service for interacting with session and local storage.
   * @param authService
   */
  constructor(
    private router: Router,
    private storageService: StorageService,
    private authService: CognitoAuthService,
  ) {}

  /**
   * Determines if the current route can be activated by verifying the user's authentication state.
   *
   * It checks AWS Cognito for a valid active session. If the user successfully logged in but
   * navigated across subdomains (e.g. prep-asis to prep-cenwood, which wipes sessionStorage),
   * this guard automatically "rehydrates" the sessionStorage by extracting the user's email,
   * LOBs, and warehouse data directly from the Cognito ID Token payload.
   *
   * @returns {Promise<boolean>} True if authenticated and session is restored, false otherwise (redirects to login).
   */
  async canActivate(): Promise<boolean> {
    try {
      const { claims } = await this.authService.currentSession();

      // The identity stored by the app must always match the session that is actually open:
      // Cognito keeps its own entry in localStorage while most screens read sessionStorage,
      // so after a user switch the previous email must be healed (SW-12).
      const sessionEmail = claims['email'] || claims['cognito:username'];
      if (sessionEmail) {
        persistSessionEmail(sessionEmail);
      }

      // Rehydrate sessionStorage if crossing subdomains
      if (!this.storageService.getItem(LOBS_DATA, STORAGE_TYPE.SESSION)) {
        const dataLOB = claims['lobs'];
        if (dataLOB) {
          this.storageService.setItem(LOBS_DATA, dataLOB, STORAGE_TYPE.SESSION);
          const lobsArray = (dataLOB || []).map((item: any) => item.lob_name);
          this.storageService.setItem(LOBS, lobsArray, STORAGE_TYPE.SESSION);

          if (dataLOB.length > 0) {
            this.storageService.setItem(
              WAREHOUSES,
              dataLOB[0].locations,
              STORAGE_TYPE.SESSION,
            );
          }
        }
      }
      return true;
    } catch (err) {
      this.router.navigate(['login'], { replaceUrl: true });
      return false;
    }
  }
}
